import { ErrorCode } from '@trulyinbox/errors';
import { ApiError } from '@/lib/api';
import { diffInCalendarDays, formatDate, toDate } from '@/lib/date';
import type { AlertBannerProps } from '@/components/molecules/AlertBanner';
import type {
  BillingBanner,
  BillingBannerKind,
  ScheduledChangeAction,
  SubscriptionScheduledChange,
} from '@/lib/billing';

/**
 * Non-component domain logic for the app-wide billing banner. The severity map,
 * the per-kind copy, and the "which CTA (if any)" decision all live here so
 * `BillingAlertBanner.tsx` stays component-only (Vite Fast Refresh) and each
 * rule is unit-testable without rendering.
 *
 * The backend owns WHICH banner shows (`BillingService.deriveBanner` — strict
 * precedence, already decided). This module only decides how the chosen banner
 * looks, reads, and behaves for the current viewer.
 */

/**
 * Days the one-time trial extension grants, for banner copy only.
 *
 * Mirrors `TRIAL_EXTENSION_DAYS` in
 * `apps/api/src/modules/billing/billing-time.constants.ts`, which is the real
 * lever — the client can't import an apps/api const, and the backend already
 * sends its own authoritative sentence on `banner.message`. This exists so the
 * number appears ONCE in this module rather than inline in each copy branch;
 * both `TRIAL_ENDING` and `TRIAL_EXTENSION_AVAILABLE` read it.
 */
const TRIAL_EXTENSION_DAYS = 7;

/**
 * Severity per kind, mapped onto the `AlertBanner` variants so the tokens stay
 * the design system's. `FREE_PLAN_UPGRADE` is deliberately `info`, not
 * `warning`: it shows for a full 30 days on every page, so anything louder
 * would be alarm fatigue rather than a nudge.
 */
export const BILLING_BANNER_VARIANT: Record<
  BillingBannerKind,
  NonNullable<AlertBannerProps['variant']>
> = {
  // `error`, not `warning`: while paused, warmup is STOPPED on every mailbox in
  // the org. That is an active outage of the product's core function, not a nudge.
  SUBSCRIPTION_PAUSED: 'error',
  PAYMENT_FAILED: 'error',
  // `warning`, not `error`: nothing is broken and nothing has been lost — the
  // plan is fully active and paid for right up to the effective date. It is a
  // heads-up with an undo, which is exactly what amber is for.
  SUBSCRIPTION_SCHEDULED_CHANGE: 'warning',
  TRIAL_ENDING: 'warning',
  // `warning`, not `info`: the trial has already ended (warmup is on free-plan
  // limits now) and the offer to revive it expires — that is a closing window,
  // not a standing nudge like FREE_PLAN_UPGRADE.
  TRIAL_EXTENSION_AVAILABLE: 'warning',
  FREE_PLAN_UPGRADE: 'info',
};

/**
 * The single action offered alongside a banner, or `null` for none.
 *
 * `null` is the correct outcome for a NON-OWNER on every kind — each CTA lands
 * on an endpoint the backend gates to `OrgRole.OWNER`
 * (`POST /billing/subscription/extend-trial`, `POST /billing/customer-portal`)
 * or on the owner-only upgrade card — so offering one to a member would only
 * buy them a 403. They get copy pointing at the owner instead.
 */
export type BillingBannerAction = 'portal' | 'extend-trial' | 'upgrade' | null;

export function billingBannerAction(
  banner: BillingBanner,
  isOrgOwner: boolean,
): BillingBannerAction {
  if (!isOrgOwner) return null;
  switch (banner.kind) {
    // No CTA. Resume lives on the billing page's lifecycle actions (with the
    // confirm dialog that explains the consequences); a one-click "Resume" here
    // would restart billing from a banner on an unrelated page. Non-owners get
    // copy pointing at the owner, like every other kind.
    case 'SUBSCRIPTION_PAUSED':
      return null;
    // Same reasoning, same answer. The undo ("Keep my subscription") lives on the
    // billing page behind a confirm dialog that spells out what carries on; a
    // one-click undo on an unrelated page would reverse a deliberate billing
    // decision with no explanation and no way to re-read it.
    case 'SUBSCRIPTION_SCHEDULED_CHANGE':
      return null;
    case 'PAYMENT_FAILED':
      return 'portal';
    case 'TRIAL_ENDING':
    // Same CTA, same reasoning — the only difference is that here the extension
    // REVIVES a trial that already lapsed rather than pushing a live one out.
    // falls through
    case 'TRIAL_EXTENSION_AVAILABLE':
      // The extension is one-time. Once it's spent the backend suppresses the
      // banner outright, so `trialExtensionAvailable: false` is defensive — but
      // if it ever does arrive, the remaining self-serve path is upgrading.
      return banner.trialExtensionAvailable ? 'extend-trial' : 'upgrade';
    case 'FREE_PLAN_UPGRADE':
      return 'upgrade';
  }
}

/** The banner's deadline as a Date, or `null` when absent / unparseable. */
function parseDeadline(graceEndsAt: string | null): Date | null {
  if (!graceEndsAt) return null;
  const date = toDate(graceEndsAt);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "today" / "tomorrow" / "in 5 days" — how far off `deadline` is from `now`. */
function relativeDeadline(deadline: Date, now: Date): string {
  const days = diffInCalendarDays(deadline, now);
  if (days <= 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
}

/**
 * What the pending change will DO, in the grammatical form this banner's
 * sentence needs ("is scheduled to be cancelled on …"). Module-private and
 * deliberately NOT merged with `scheduledChangeLine`'s third-person verb map in
 * `lifecycle-helpers` ("Cancels on …"): the two render different sentences on
 * different surfaces, so collapsing them would force one of the two to read
 * worse. Same enum, genuinely different copy.
 */
const SCHEDULED_CHANGE_PHRASE: Record<ScheduledChangeAction, string> = {
  cancel: 'be cancelled',
  pause: 'pause',
  resume: 'resume',
};

/**
 * The banner's user-facing sentence.
 *
 * Client-owned rather than the backend's `banner.message` — a deliberate
 * divergence, not an oversight. The server string embeds a raw `2026-07-27` ISO
 * slice (the app formats dates as "Jul 27, 2026" everywhere else) and is written
 * for the owner ("Update your payment method"), which is wrong for the members
 * who also see this banner and have no such control.
 *
 * `scheduledChange` comes from the summary alongside `banner`, not from `banner`
 * itself — the server puts the effective date on `banner.graceEndsAt` but keeps
 * the ACTION (pause vs cancel) on the sibling field, and this copy needs both.
 * It is a required parameter rather than an optional one so a new call site
 * can't silently render the pause wording for a cancellation.
 *
 * `FREE_PLAN_UPGRADE` intentionally shows NO date: its `graceEndsAt` is when the
 * nudge stops being displayed, not a deadline the user loses anything by —
 * surfacing it would invent a cliff that doesn't exist.
 */
export function billingBannerMessage(
  banner: BillingBanner,
  isOrgOwner: boolean,
  scheduledChange: SubscriptionScheduledChange | null,
  now: Date = new Date(),
): string {
  const deadlineAt = parseDeadline(banner.graceEndsAt);

  switch (banner.kind) {
    // Deliberately NO date: a pause is indefinite (it ends when the customer
    // resumes), so there is no deadline and naming one would invent a cliff.
    //
    // Leads with view-only, because that is the broader surprise: every
    // org-changing control in the panel is disabled, not just warmup. The
    // manual re-enable still follows it — it is the part users are most likely
    // to be blindsided by, especially anyone who paused from the Paddle portal,
    // which never mentioned mailboxes — so both facts are in both variants.
    case 'SUBSCRIPTION_PAUSED':
      return isOrgOwner
        ? 'Your account is view-only while your subscription is paused, and warmup is stopped on all mailboxes. Resume it from Billing, then re-enable each mailbox to start warming again.'
        : "Your account is view-only while your organization's subscription is paused, and warmup is stopped on all mailboxes. Ask your workspace owner to resume it. Each mailbox then needs re-enabling.";
    // The counterpart to SUBSCRIPTION_PAUSED: there, the pause has ALREADY
    // happened; here it is merely booked and the plan is untouched until the
    // date. The copy leads with what still works ("full access until then")
    // because the failure mode this banner exists to fix was the opposite — a
    // scheduled pause that was invisible until the day it bit.
    case 'SUBSCRIPTION_SCHEDULED_CHANGE': {
      // The action is the whole point of the sentence. If the summary somehow
      // arrives without it, say something true and unspecific rather than
      // guessing "pause" and being wrong half the time.
      const what = scheduledChange
        ? `is scheduled to ${SCHEDULED_CHANGE_PHRASE[scheduledChange.action]}`
        : 'has a change scheduled';
      const when = deadlineAt
        ? ` on ${formatDate(deadlineAt)}`
        : ' at the end of your current period';
      return isOrgOwner
        ? `Your subscription ${what}${when}. You keep full access until then. Go to Billing to keep it running.`
        : `Your organization's subscription ${what}${when}. You keep full access until then. Ask your workspace owner if that wasn't intended.`;
    }
    case 'PAYMENT_FAILED': {
      const by = deadlineAt ? ` by ${formatDate(deadlineAt)}` : '';
      return isOrgOwner
        ? `Your last payment failed. Update your payment method${by} to keep your subscription active.`
        : `Your organization's last payment failed. Ask your workspace owner to update the payment method${by} to keep your subscription active.`;
    }
    case 'TRIAL_ENDING': {
      const when = deadlineAt
        ? `${relativeDeadline(deadlineAt, now)} (${formatDate(deadlineAt)})`
        : 'soon';
      const lead = `Your trial ends ${when}.`;
      if (!isOrgOwner) {
        return `${lead} Ask your workspace owner to upgrade to keep warming up.`;
      }
      return banner.trialExtensionAvailable
        ? `${lead} Extend it by ${TRIAL_EXTENSION_DAYS} days or upgrade to keep warming up.`
        : `${lead} Upgrade to keep warming up.`;
    }
    // The trial has ALREADY ended and the org is on free-plan limits — so this
    // leads with that fact rather than a countdown to it. The date shown is the
    // deadline for taking the extension, which is a real one (unlike
    // FREE_PLAN_UPGRADE's, which is only when the nudge stops).
    case 'TRIAL_EXTENSION_AVAILABLE': {
      const by = deadlineAt ? ` until ${formatDate(deadlineAt)}` : '';
      if (!isOrgOwner) {
        return `Your trial has ended. Ask your workspace owner to extend it or upgrade to keep warming up.`;
      }
      return banner.trialExtensionAvailable
        ? `Your trial has ended. You can still extend it by ${TRIAL_EXTENSION_DAYS} days${by}, or upgrade to keep warming up.`
        : 'Your trial has ended. Upgrade to keep warming up.';
    }
    case 'FREE_PLAN_UPGRADE':
      return isOrgOwner
        ? "You're on the Free plan. Upgrade to raise your mailbox and warmup limits."
        : "You're on the Free plan. Ask your workspace owner to upgrade for higher mailbox and warmup limits.";
  }
}

/**
 * `true` when a trial extension failed because the one-time extension is already
 * spent (`041202 AlreadyExtended`). Reachable in normal use: the summary is
 * cached for 60s, so a second tab (or a stale banner) can still offer the CTA
 * after the extension has landed. Branch on the catalog code, never the message.
 */
export function isTrialAlreadyExtended(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.errorCode === ErrorCode.Billing.TrialExtension.AlreadyExtended
  );
}
